// Pass 2 merge-queue data layer (PRD 00 §Pass 2 trigger modes).
// TanStack Query hooks for the MergeQueuePage.

import { apiFetch } from "../../lib/apiClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Definition } from "../../data/types";


export type MergeKind = "auto_merge" | "fuzzy_match" | "split";
export type MergeReviewState = "pending" | "accepted" | "rejected" | "edited";

export interface MergeProposal {
  proposal_id: string;
  corpus_id: string;
  kind: MergeKind;
  cluster_member_refs: string[];
  proposed_canonical: Definition | null;
  confidence: number;
  created_at: string;
  review_state: MergeReviewState;
}

export interface PendingMerges {
  corpus_id: string;
  reconcile_version: number;
  proposals: MergeProposal[];
  last_run_at: string;
}

export type EntityKind = "term" | "symbol";

export interface CorpusEntity {
  entity_id: string;
  corpus_id: string;
  kind: EntityKind;
  name: string;
  alternates: string[];
  definition: Definition;
  member_refs: string[];
  structural: Record<string, unknown>;
  confidence: number | null;
  is_orphan: boolean;
  last_reconciled_at: string;
}

export interface CorpusGlossary {
  corpus_id: string;
  reconcile_version: number;
  entities: CorpusEntity[];
  prereq_dag: Record<string, string[]>;
  embedded_at: string;
}

export interface ReconciliationRun {
  corpus_id: string;
  mode: "full" | "incremental";
  reconcile_version: number;
  candidates_count: number;
  clusters_formed: number;
  auto_merges_proposed: number;
  fuzzy_proposed: number;
  canonical_entities: number;
  started_at: string;
  finished_at: string;
}

export interface RejectionLedger {
  corpus_id: string;
  pairs: Array<[string, string]>;
}

const mergesKey = (corpusId: string) =>
  ["corpus-merges", corpusId] as const;
const glossaryKey = (corpusId: string) =>
  ["corpus-glossary", corpusId] as const;
const rejectionsKey = (corpusId: string) =>
  ["corpus-rejections", corpusId] as const;

export function useCorpusMerges(corpusId: string) {
  return useQuery<PendingMerges, Error>({
    queryKey: mergesKey(corpusId),
    queryFn: async () => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/merges`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) throw new Error(`merges fetch failed: ${r.status}`);
      return (await r.json()) as PendingMerges;
    },
    staleTime: 15_000,
  });
}

export function useCorpusGlossary(corpusId: string) {
  return useQuery<CorpusGlossary | null, Error>({
    queryKey: glossaryKey(corpusId),
    queryFn: async () => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/glossary`,
        { headers: { Accept: "application/json" } },
      );
      if (r.status === 404) return null;
      if (!r.ok) throw new Error(`glossary fetch failed: ${r.status}`);
      return (await r.json()) as CorpusGlossary;
    },
    staleTime: 30_000,
  });
}

export function useCorpusRejections(corpusId: string) {
  return useQuery<RejectionLedger, Error>({
    queryKey: rejectionsKey(corpusId),
    queryFn: async () => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/rejections`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) throw new Error(`rejections fetch failed: ${r.status}`);
      return (await r.json()) as RejectionLedger;
    },
    staleTime: 30_000,
  });
}

export function useReconcileCorpus(corpusId: string) {
  const qc = useQueryClient();
  return useMutation<ReconciliationRun, Error, void>({
    mutationFn: async () => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/reconcile`,
        { method: "POST", headers: { Accept: "application/json" } },
      );
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`reconcile failed (${r.status}): ${detail}`);
      }
      return (await r.json()) as ReconciliationRun;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: mergesKey(corpusId) });
      void qc.invalidateQueries({ queryKey: glossaryKey(corpusId) });
    },
  });
}

type DecisionAction = "accept" | "reject";

interface DecideArgs {
  proposalId: string;
  action: DecisionAction;
  actor?: string;
}

export function useDecideMerge(corpusId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, DecideArgs>({
    mutationFn: async ({ proposalId, action, actor = "anonymous" }) => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/merges/${encodeURIComponent(
          proposalId,
        )}/${action}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ actor }),
        },
      );
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`${action} failed (${r.status}): ${detail}`);
      }
      return (await r.json()) as unknown;
    },
    onMutate: async ({ proposalId, action }) => {
      await qc.cancelQueries({ queryKey: mergesKey(corpusId) });
      const previous = qc.getQueryData<PendingMerges>(mergesKey(corpusId));
      if (previous) {
        qc.setQueryData<PendingMerges>(mergesKey(corpusId), {
          ...previous,
          proposals: previous.proposals.map((p) =>
            p.proposal_id === proposalId
              ? { ...p, review_state: action === "accept" ? "accepted" : "rejected" }
              : p,
          ),
        });
      }
      return { previous };
    },
    onError: (_e, _vars, ctx) => {
      const prev = (ctx as { previous?: PendingMerges } | undefined)?.previous;
      if (prev) qc.setQueryData(mergesKey(corpusId), prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: mergesKey(corpusId) });
      // Reject writes the rejection ledger too — invalidate that view.
      void qc.invalidateQueries({ queryKey: rejectionsKey(corpusId) });
    },
  });
}
